import type { ToneNotation } from "@kensio/pinyinjs";

import type { CanonicalEntry } from "../segmenting/canonical-entry.js";
import { element } from "./dom.js";
import type { SegmentedWord } from "../segmenting/segmented-words.js";
import { shortGloss, wholeGloss } from "./segmenter-glosses.js";
import { markPinyinWord } from "./syllable-text.js";
import { toneClass } from "../segmenting/tone-class.js";

/**
 * The three cells of a row that hold more than a string.
 *
 * Apart from `segmenter-rows.ts`, which lays the row out. Each of these builds
 * a run of nodes for one cell, and they are here because a cell that is
 * sometimes a link, a cell that is one span per syllable and a cell that opens
 * are the same kind of problem.
 */

/**
 * The word's pinyin, one coloured span per syllable.
 *
 * Per syllable rather than as one string, because the site colours pinyin by
 * tone and has since long before these tools existed. `markPinyinWord` puts
 * the 隔音符号 on the boundary that takes it, which is why the apostrophe
 * cannot be added by writing each syllable alone.
 *
 * The reading is the one the decoder settled on for this occurrence, so 得
 * after a verb says `de` here whatever 得 says elsewhere in the same text.
 */
export function pinyinNodes(
  word: SegmentedWord,
  notation: ToneNotation,
): readonly Node[] {
  return markPinyinWord(word.reading, notation).map((text, at) => {
    const tone = toneClass(word.reading[at]?.tone);

    return tone === undefined
      ? document.createTextNode(text)
      : element("span", { text, className: tone });
  });
}

/**
 * The characters, as a link to the 词典 entry where the site has one.
 *
 * A word with no entry is drawn as the text it always was. 92.7% of the
 * distinct words in running text have a page, and the rest are mostly names
 * and compounds: linking those would send a reader to a 404 for the sake of
 * looking consistent. See `cidian-index.ts` for how a page tells them apart.
 */
export function hanziNodes(word: SegmentedWord): readonly Node[] {
  return [
    word.address === undefined
      ? document.createTextNode(word.text)
      : element("a", {
          text: word.text,
          className: "cb-segmenter-entry",
          attributes: { href: word.address },
        }),
  ];
}

/**
 * What the word means, as a control that opens.
 *
 * Closed, the cell shows the first few of the entry's meanings on one line,
 * cut with an ellipsis by `modules/tools`. A list of a few hundred rows is
 * read by scanning down it, and a row that grew to four lines because
 * CC-CEDICT has nine glosses for 打 would break that.
 *
 * **Opening it is what the site counts as looking the word up.** The gloss
 * arriving in the row is the page answering a question nobody asked, and every
 * word in a pasted article would be a history entry. Pressing is a reader
 * saying they wanted this one. `SegmenterLookups` is what records it.
 *
 * An entry with a single meaning opens to the same words unclamped. That is
 * the price of one control meaning one thing on every row that has an entry.
 *
 * The button is the whole cell rather than a mark beside the text, so the
 * target is the width of the column.
 */
export function glossNodes(
  entry: CanonicalEntry | undefined,
  open: boolean,
  word: string,
): readonly Node[] {
  // Nothing, in the two cases that look the same on the page. The entry has
  // not arrived yet, and the entry arrived carrying no gloss at all.
  if (entry === undefined || entry.meanings.length === 0) {
    return [];
  }

  return [
    element("button", {
      className: "cb-segmenter-meaning",
      text: open ? wholeGloss(entry) : shortGloss(entry),
      attributes: {
        type: "button",
        "aria-expanded": String(open),
        "aria-label": `${open ? "Close" : "Open"} the meaning of ${word}`,
      },
    }),
  ];
}

/**
 * The meaning's cell, which is empty until the entry behind it arrives.
 *
 * The cell carries the headword so that `SegmenterMeanings` can find it and
 * fill it in place. A word the 词典 has no page for carries nothing, there
 * being no entry on its way.
 */
export function glossCell(
  word: SegmentedWord,
  entry: CanonicalEntry | undefined,
  open: boolean,
): HTMLTableCellElement {
  return element("td", {
    className: "cb-segmenter-gloss",
    attributes: {
      "data-cb-gloss": word.address === undefined ? undefined : word.headword,
    },
    children: glossNodes(entry, open, word.text),
  });
}
